"use client";

import { useMemo, useEffect, useState, useRef, useCallback, Suspense, useTransition } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { searchProducts } from '@/utils/productSearch';
import { IoSearchOutline } from 'react-icons/io5';

import { Product, Category, Purpose } from '@/types';
import ProductGrid from '@/components/ProductGrid';
import SidebarFilter from '@/components/SidebarFilter';
import Breadcrumbs from '@/components/Breadcrumbs';
import BreadcrumbsPlaceholder from '@/components/BreadcrumbsPlaceholder';
import ProductCardSkeleton from '@/components/ProductCardSkeleton';
import { sortProductsByNewest } from '@/utils/productUtils';
import { productHasTag } from '@/utils/productTags';
import type { CollectionSeo } from '@/lib/editorial';

export type ProductsClientProps = {
    allProducts: Product[];
    categories: Category[];
    purposes: Purpose[];
    serverFilteredTag: string | null;
    totalProductCount: number;
    initialCollectionSeo: CollectionSeo | null;
    hideHeader?: boolean;
    hideBreadcrumbs?: boolean;
};

function ProductsClientInner({
    allProducts,
    categories,
    purposes,
    serverFilteredTag,
    totalProductCount,
    initialCollectionSeo,
    hideHeader = false,
    hideBreadcrumbs = false,
}: ProductsClientProps) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [, startTransition] = useTransition();

    const activeTag = searchParams.get('byTag');
    const searchQuery = searchParams.get('query');

    // Determine if the active tag is a category or purpose for UI highlighting
    const isCategory = categories.some(c => c.tag === activeTag);
    const isPurpose = purposes.some(p => p.tag === activeTag);

    const selectedCategory = isCategory ? activeTag : null;
    const selectedPurpose = isPurpose ? activeTag : null;

    const [sortBy, setSortBy] = useState<string>('createdAt');
    const [order, setOrder] = useState<string>('desc');
    const [displayCount, setDisplayCount] = useState(20);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [remoteProducts, setRemoteProducts] = useState<Product[]>(allProducts);
    const [remoteTotalCount, setRemoteTotalCount] = useState(totalProductCount);
    const hasServerPagination = Boolean(activeTag && activeTag === serverFilteredTag);

    // --- Filtering & Sorting Logic ---
    const filteredProducts = useMemo(() => {
        let result = [...allProducts];

        // 1. FILTER by Tag (Category/Purpose/Generic)
        if (activeTag) {
            result = result.filter(p => productHasTag(p.tags, activeTag, p.name, p.slug));
        }

        // 2. SEARCH (Fuzzy with weighted relevance & title priority)
        if (searchQuery) {
            result = searchProducts(result, searchQuery);
        }

        // 3. SORT
        if (sortBy === 'price' || sortBy === 'discountedPrice') {
            result.sort((a, b) => {
                if (order === 'asc') return a.price - b.price;
                return b.price - a.price;
            });
        } else {
            result = sortProductsByNewest(result);
        }

        return result;
    }, [allProducts, activeTag, searchQuery, sortBy, order]);

    // --- Infinite Scroll Slice ---
    const productsToDisplay = hasServerPagination ? remoteProducts : filteredProducts.slice(0, displayCount);
    const hasMore = hasServerPagination
        ? remoteProducts.length < remoteTotalCount
        : displayCount < filteredProducts.length;
    const currentSortOption = sortBy === 'price' ? `price-${order}` : null;
    const hasBrowseContext = Boolean(activeTag || searchQuery);

    const fetchTagPage = useCallback(async (offset: number, nextSort = sortBy, nextOrder = order) => {
        if (!activeTag) throw new Error('A product tag is required.');

        const params = new URLSearchParams({
            byTag: activeTag,
            offset: String(offset),
            sort: nextSort === 'price' ? 'price' : 'createdAt',
            order: nextOrder,
        });
        const response = await fetch(`/api/products?${params.toString()}`);
        if (!response.ok) throw new Error('Unable to load more products.');
        return response.json() as Promise<{ products: Product[]; totalCount: number }>;
    }, [activeTag, order, sortBy]);

    const loadMore = useCallback(() => {
        if (isLoadingMore || !hasMore) return;
        
        setIsLoadingMore(true);
        setLoadError(null);
        if (hasServerPagination) {
            fetchTagPage(remoteProducts.length)
                .then(({ products, totalCount }) => {
                    setRemoteProducts(previous => [...previous, ...products]);
                    setRemoteTotalCount(totalCount);
                })
                .catch((error) => {
                    console.error('Unable to load more tag products', error);
                    setLoadError('We could not load more products. Please try again.');
                })
                .finally(() => setIsLoadingMore(false));
        } else {
            setTimeout(() => {
                setDisplayCount(prev => prev + 20);
                setIsLoadingMore(false);
            }, 1500);
        }
    }, [fetchTagPage, hasMore, hasServerPagination, isLoadingMore, remoteProducts.length]);

    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTag, searchQuery]);

    const handleCategoryChange = (categoryTag: string | null) => {
        const params = new URLSearchParams();
        if (categoryTag) {
            params.set('byTag', categoryTag);
        }
        startTransition(() => {
            router.replace(`${pathname}?${params.toString()}`);
        });
    };

    const handlePurposeChange = (purposeTag: string | null) => {
        const params = new URLSearchParams();
        if (purposeTag) {
            params.set('byTag', purposeTag);
        }
        startTransition(() => {
            router.replace(`${pathname}?${params.toString()}`);
        });
    };

    const handleSortChange = (sortOption: string | null) => {
        const nextSort = sortOption === 'price-asc' || sortOption === 'price-desc' ? 'price' : 'createdAt';
        const nextOrder = sortOption === 'price-asc' ? 'asc' : 'desc';

        startTransition(() => {
            setSortBy(nextSort);
            setOrder(nextOrder);
        });

        if (!hasServerPagination) return;

        setIsLoadingMore(true);
        setLoadError(null);
        fetchTagPage(0, nextSort, nextOrder)
            .then(({ products, totalCount }) => {
                setRemoteProducts(products);
                setRemoteTotalCount(totalCount);
            })
            .catch((error) => {
                console.error('Unable to sort tag products', error);
                setLoadError('We could not update the sort order. Please try again.');
            })
            .finally(() => setIsLoadingMore(false));
    };

    const handleClearFilters = () => {
        startTransition(() => {
            router.replace(pathname);
            setSortBy('createdAt');
            setOrder('desc');
        });
    };

    const headingTitle = useMemo(() => {
        if (searchQuery) return `Search results for "${searchQuery}"`;
        if (activeTag) {
            if (initialCollectionSeo?.tag === activeTag) return initialCollectionSeo.h1;
            const cat = categories.find(c => c.tag === activeTag);
            if (cat) return cat.name;
            const purp = purposes.find(p => p.tag === activeTag);
            if (purp) return `For ${purp.name}`;
            return activeTag; // Fallback
        }
        return "Astroji Mall";
    }, [activeTag, searchQuery, categories, purposes, initialCollectionSeo]);

    const headingSubtitle = useMemo(() => {
        if (searchQuery) return `Found ${filteredProducts.length} items matching your search.`;
        if (activeTag) return initialCollectionSeo?.tag === activeTag ? initialCollectionSeo.intro_markdown : `Explore our curated selection for your spiritual needs.`;
        return "Discover tools for your spiritual journey, curated with intention and care.";
    }, [activeTag, searchQuery, filteredProducts.length, initialCollectionSeo]);

    const loaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && hasMore && !loadError) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );

        const currentLoader = loaderRef.current;
        if (currentLoader) {
            observer.observe(currentLoader);
        }
        return () => {
            if (currentLoader) {
                observer.unobserve(currentLoader);
            }
        };
    }, [hasMore, loadError, loadMore]);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 400);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const breadcrumbItems = useMemo(() => {
        if (activeTag) {
            return [
                { label: 'Products', href: '/products' },
                { label: headingTitle }
            ];
        }
        return [
            { label: 'Products' }
        ];
    }, [activeTag, headingTitle]);

    return (
        <div className="bg-background py-4 lg:py-6">
            <div className="ui-container-wide">
                {!hideBreadcrumbs && <Breadcrumbs items={breadcrumbItems} className="mb-3" />}
                {!hideHeader && (
                    <div className="mb-5 rounded-2xl border border-orange-100 bg-white px-4 py-6 text-center shadow-[0_1px_12px_rgba(15,23,42,0.04)] lg:mb-6 lg:px-6 lg:py-7">
                        <div className="mx-auto max-w-3xl">
                            <h1 className="text-3xl font-semibold leading-tight text-accent md:text-4xl">{headingTitle}</h1>
                            <span className="mx-auto mt-3 block h-0.5 w-14 rounded-full bg-primary/70"></span>
                            {headingSubtitle && (
                                <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-text-secondary md:text-base">
                                    {headingSubtitle}
                                </p>
                            )}
                        </div>
                        {hasBrowseContext && (
                            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
                                <span className="ui-pill border-orange-100 bg-orange-50 text-primary">
                                    {(hasServerPagination ? remoteTotalCount : filteredProducts.length)} result{(hasServerPagination ? remoteTotalCount : filteredProducts.length) === 1 ? '' : 's'}
                                </span>
                                <button
                                    onClick={handleClearFilters}
                                    className="ui-pill transition-colors hover:border-primary/40 hover:text-primary"
                                >
                                    Clear filters
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-col gap-4 lg:flex-row lg:gap-5 xl:gap-6">
                    <SidebarFilter
                        categories={categories}
                        purposes={purposes}
                        selectedCategory={selectedCategory}
                        selectedPurpose={selectedPurpose}
                        sortOption={currentSortOption}
                        onCategoryChange={handleCategoryChange}
                        onPurposeChange={handlePurposeChange}
                        onSortChange={handleSortChange}
                        onClearFilters={handleClearFilters}
                        className="sticky top-[80px]"
                    />

                    <div className="min-w-0 flex-1 min-h-[420px]">
                        {productsToDisplay.length > 0 ? (
                            <>
                                <ProductGrid products={productsToDisplay} loading={false} />

                                <div ref={loaderRef} className="h-24 flex items-center justify-center mt-4">
                                    {isLoadingMore && (
                                        <div className="flex flex-col items-center gap-2 transition-opacity duration-300">
                                            <div className="w-8 h-8 border-4 border-gray-200 border-t-primary rounded-full animate-loader-spin"></div>
                                            <span className="text-xs text-gray-500 font-medium tracking-wide animate-pulse">LOADING MORE...</span>
                                        </div>
                                    )}
                                    {loadError && !isLoadingMore && (
                                        <div className="text-center">
                                            <p className="mb-3 text-sm text-red-600">{loadError}</p>
                                            <button onClick={loadMore} className="ui-button-soft border-primary/40 text-primary hover:bg-primary hover:text-white">
                                                Try again
                                            </button>
                                        </div>
                                    )}
                                    {!hasMore && !isLoadingMore && !loadError && productsToDisplay.length > 0 && (
                                        <div className="text-center">
                                            <p className="text-sm text-gray-400 font-medium mb-4">You&apos;ve reached the end</p>
                                            {hasBrowseContext && (
                                                <button
                                                    onClick={handleClearFilters}
                                                    className="ui-button-accent"
                                                >
                                                    View All Products
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="ui-card border-dashed py-12 text-center">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 text-primary">
                                    <IoSearchOutline size={24} />
                                </div>
                                <h3 className="mb-2 text-xl font-semibold text-accent">No products found</h3>
                                <p className="mx-auto mb-6 max-w-md text-sm leading-6 text-gray-500">
                                    By {searchQuery ? `"${searchQuery}"` : activeTag || 'this category'}. Try adjusting your filters.
                                </p>
                                <button
                                    onClick={handleClearFilters}
                                    className="ui-button-soft border-primary/40 text-primary hover:bg-primary hover:text-white"
                                >
                                    Clear all filters
                                </button>
                                <a
                                    href="https://forms.gle/yLzWDW82UAj35Koj6"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block mt-4 text-sm text-gray-400 hover:text-primary transition-colors underline"
                                >
                                    Looking for something specific? Let us know!
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <button
                onClick={scrollToTop}
                className={`fixed bottom-5 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-accent text-white shadow-[0_8px_24px_rgba(15,23,42,0.18)] transition-all duration-200 active:scale-95 ${showScrollTop ? 'translate-y-0 opacity-100 visible' : 'translate-y-10 opacity-0 invisible'}`}
                aria-label="Scroll to top"
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
            </button>
        </div>
    );
}

export default function ProductsClient(props: ProductsClientProps) {
    const catalogKey = `${props.serverFilteredTag || 'all'}:${props.totalProductCount}:${props.allProducts.length}:${props.allProducts[0]?.id || ''}`;

    return (
        <Suspense fallback={<ProductsLoadingState hideHeader={props.hideHeader} hideBreadcrumbs={props.hideBreadcrumbs} />}>
            <ProductsClientInner key={catalogKey} {...props} />
        </Suspense>
    );
}

function ProductsLoadingState({ hideHeader = false, hideBreadcrumbs = false }: { hideHeader?: boolean; hideBreadcrumbs?: boolean }) {
    return (
        <div className="bg-background py-4 lg:py-6" aria-busy="true" aria-label="Loading products">
            <div className="ui-container-wide">
                {!hideBreadcrumbs && <BreadcrumbsPlaceholder className="mb-3" />}
                {!hideHeader && (
                    <div className="mb-5 rounded-2xl border border-orange-100 bg-white px-4 py-6 text-center shadow-[0_1px_12px_rgba(15,23,42,0.04)] lg:mb-6 lg:px-6 lg:py-7">
                        <div className="mx-auto h-10 w-64 rounded bg-gray-200 motion-safe:animate-pulse" />
                        <div className="mx-auto mt-3 h-0.5 w-14 rounded bg-orange-100" />
                        <div className="mx-auto mt-3 h-6 w-96 max-w-full rounded bg-gray-100 motion-safe:animate-pulse" />
                    </div>
                )}
                <div className="flex flex-col gap-4 lg:flex-row lg:gap-5 xl:gap-6">
                    <div className="hidden w-[238px] flex-shrink-0 lg:block xl:w-60 2xl:w-64">
                        <div className="ui-card min-h-[480px] p-4 xl:p-5 motion-safe:animate-pulse bg-white rounded-xl border border-gray-100" />
                    </div>
                    <div className="min-w-0 flex-1 min-h-[420px]">
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                            {Array.from({ length: 10 }).map((_, index) => (
                                <ProductCardSkeleton key={index} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
